import * as d3 from 'd3';
import { createNumberTooltip, formatValue, GroupedValueNameData, roundDigitsAfterDecimal, sidelyChartColors, ValueNameData } from '../cross_platform/dashboard_script';
import { GreySidely3, LightGreySidely2 } from '../../../styles/global/css/Utils';
import { TitleFontFamily } from '../../../styles/global/css/GlobalText';

function resolveValue(chartData: ValueNameData | GroupedValueNameData[]): { value: number, entries: ValueNameData[] } {
	if (!Array.isArray(chartData)) return { value: chartData.value, entries: [chartData] };
	const namesAreDates = chartData.length > 0 && chartData.every(d => typeof d.name === 'string' && !isNaN(new Date(d.name).getTime()));
	const value = namesAreDates
		? chartData[chartData.length - 1].value
		: chartData.reduce((acc, d) => acc + (typeof d.value === 'number' ? d.value : 0), 0);
	return { value, entries: chartData };
}

const VALUE_FONT_SIZE = 30;
const MIN_VALUE_FONT_SIZE = 12;

function fitFontSize(text: string, maxWidth: number, maxHeight: number): number {
	const context = document.createElement('canvas').getContext('2d');
	let textWidth = text.length * VALUE_FONT_SIZE * 0.6;
	if (context) {
		context.font = `500 ${VALUE_FONT_SIZE}px ${TitleFontFamily}`;
		textWidth = context.measureText(text).width;
	}
	const widthScale = textWidth > 0 ? maxWidth / textWidth : 1;
	const heightScale = maxHeight / (VALUE_FONT_SIZE * 0.75);
	const size = VALUE_FONT_SIZE * Math.min(1, widthScale, heightScale);
	return Math.max(MIN_VALUE_FONT_SIZE, Math.round(size));
}

export function createGaugeDiv({
	chartData,
	width,
	height,
	margin,
	opts,
	tooltipId
}: {
	chartData: ValueNameData | GroupedValueNameData[],
	width: number,
	height: number,
	margin: number,
	opts?: {
		locale?: string,
		number_extension?: string | number,
		digits_after_decimal?: number,
		min?: number,
		max?: number
	},
	tooltipId?: string
}): { container: HTMLDivElement; cleanup: () => void } | null {
	const { value, entries } = resolveValue(chartData);
	if (entries.length === 0) return null;
	const min = opts?.min ?? 0;
	const max = opts?.max ?? 100;
	const ratio = max > min ? Math.min(1, Math.max(0, (value - min) / (max - min))) : 0;
	const format = (v: number) => formatValue(roundDigitsAfterDecimal(v, opts?.digits_after_decimal), opts);

	const radius = Math.max(40, Math.min(width / 2 - margin, height * 0.5, 160));
	const thickness = radius * 0.2;
	const labelHeight = 18;
	const svgWidth = radius * 2 + margin * 2;
	const svgHeight = radius + margin + labelHeight;
	const startAngle = -Math.PI / 2;

	const container = document.createElement('div');
	Object.assign(container.style, {
		width: `${width}px`,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		fontFamily: '\'Inter\', sans-serif'
	});

	const svg = d3.create('svg')
		.attr('width', svgWidth)
		.attr('height', svgHeight)
		.attr('viewBox', [0, 0, svgWidth, svgHeight]);
	const g = svg.append('g').attr('transform', `translate(${margin + radius},${margin + radius})`);
	const midRadius = radius - thickness / 2;
	const point = (r: number, angle: number) => `${r * Math.sin(angle)} ${-r * Math.cos(angle)}`;
	const roundedEndArc = (end: number) => {
		const capAngle = Math.min(Math.asin(thickness / 2 / midRadius), end - startAngle);
		const cap = midRadius * Math.sin(capAngle);
		const capEnd = end - capAngle;
		const outer = midRadius + cap;
		const inner = midRadius - cap;
		return `M${point(outer, startAngle)}A${outer} ${outer} 0 0 1 ${point(outer, capEnd)}A${cap} ${cap} 0 0 1 ${point(inner, capEnd)}A${inner} ${inner} 0 0 0 ${point(inner, startAngle)}Z`;
	};

	g.append('path')
		.attr('fill', LightGreySidely2)
		.attr('d', roundedEndArc(Math.PI / 2));
	g.append('path')
		.attr('fill', sidelyChartColors[0])
		.attr('d', roundedEndArc(startAngle))
		.transition()
		.duration(600)
		.ease(d3.easeCubicOut)
		.attrTween('d', () => {
			const interpolate = d3.interpolate(startAngle, startAngle + Math.PI * ratio);
			return t => roundedEndArc(interpolate(t));
		});

	const valueText = format(value);
	const valueBaseline = 6;
	const valueFontSize = fitFontSize(valueText, (radius - thickness) * 2 - 8, radius - thickness - valueBaseline);
	g.append('text')
		.attr('text-anchor', 'middle')
		.attr('y', -valueBaseline)
		.attr('fill', 'currentColor')
		.style('font-family', TitleFontFamily)
		.style('font-weight', '500')
		.style('font-size', `${valueFontSize}px`, 'important')
		.style('letter-spacing', '-0.01em')
		.text(valueText);

	const labelY = labelHeight - 4;
	const labelX = radius - thickness / 2;
	[[-labelX, min], [labelX, max]].forEach(([x, bound]) => {
		g.append('text')
			.attr('text-anchor', 'middle')
			.attr('x', x)
			.attr('y', labelY)
			.attr('fill', GreySidely3)
			.style('font-size', '11px', 'important')
			.text(format(bound));
	});

	container.appendChild(svg.node()!);

	const { cleanup } = createNumberTooltip(
		container,
		() => entries
			.map(d => `<div><span style="opacity:0.9">${d.name}</span> : <span style="font-weight:600">${format(d.value)}</span></div>`)
			.join(''),
		{ tooltipId }
	);

	return { container, cleanup };
}
